import * as React from 'react'

import { F0Alert, F0Dialog, F0Select, createDataSourceDefinition } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import {
  classifyField,
  formatGroupTitle,
  type FieldSelectionField,
} from 'modules/universal_importer/components/FieldSelectionModal'
import { resolveRequiredDependencies } from 'modules/universal_importer/lib/resolveRequiredDependencies'

// --- Types ---

export interface LegalEntityInfo {
  id: string
  legalName: string
  country: string
}

export interface EmployeeInfo {
  id: string
  firstName: string
  lastName: string
  legalEntityId: string | null
}

export interface UnifiedEditionModalResult {
  selectedEmployeeIds: string[]
  selectedFields: string[]
  /** null means cross-LE (multiple LEs selected) */
  legalEntityIds: string[] | null
  isMultiLe: boolean
}

interface UnifiedEditionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (result: UnifiedEditionModalResult) => void
  legalEntities: LegalEntityInfo[]
  employees: EmployeeInfo[]
  fields: FieldSelectionField[]
}

// --- Employee record type for F0Select source ---

type EmployeeRecord = {
  id: string
  name: string
  legalEntityId: string
  legalEntityName: string
}

// --- Field record type for F0Select source ---

type FieldRecord = {
  id: string
  name: string
  groupKey: string
  groupLabel: string
  disabled: boolean
}

// --- Component ---

export const UnifiedEditionModal: React.FC<UnifiedEditionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  legalEntities,
  employees,
  fields,
}) => {
  const visibleFields = React.useMemo(() => fields.filter((f) => !f.hidden), [fields])

  // Employee selection state
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<string[]>(() =>
    employees.filter((e) => e.legalEntityId).map((e) => e.id)
  )

  // Field selection state
  const [selectedFieldKeys, setSelectedFieldKeys] = React.useState<string[]>(() =>
    visibleFields.map((f) => f.key)
  )

  // Reset selections when inputs change
  React.useEffect(() => {
    setSelectedEmployeeIds(employees.filter((e) => e.legalEntityId).map((e) => e.id))
  }, [employees])

  React.useEffect(() => {
    setSelectedFieldKeys(visibleFields.map((f) => f.key))
  }, [visibleFields])

  // Precompute employee lookup map for O(1) access
  const employeesById = React.useMemo(() => {
    const map = new Map<string, EmployeeInfo>()
    for (const emp of employees) {
      map.set(emp.id, emp)
    }
    return map
  }, [employees])

  // Derived: which LEs are currently selected
  const selectedLeIds = React.useMemo(() => {
    const leIds = new Set<string>()
    for (const empId of selectedEmployeeIds) {
      const emp = employeesById.get(empId)
      if (emp?.legalEntityId) leIds.add(emp.legalEntityId)
    }
    return leIds
  }, [selectedEmployeeIds, employeesById])

  const isMultiLe = selectedLeIds.size > 1

  // When isMultiLe changes to true, remove LE-dependent fields from selection
  React.useEffect(() => {
    if (isMultiLe) {
      setSelectedFieldKeys((prev) =>
        prev.filter((key) => {
          const field = visibleFields.find((f) => f.key === key)
          return field && !field.leDependant
        })
      )
    }
  }, [isMultiLe, visibleFields])

  // --- Employee data source ---
  const leNameMap = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const le of legalEntities) {
      map.set(le.id, le.legalName)
    }
    return map
  }, [legalEntities])

  const employeeRecords: EmployeeRecord[] = React.useMemo(
    () =>
      employees
        .filter((e) => e.legalEntityId)
        .map((e) => ({
          id: e.id,
          name: `${e.firstName} ${e.lastName}`,
          legalEntityId: e.legalEntityId ?? '',
          legalEntityName: leNameMap.get(e.legalEntityId ?? '') ?? '',
        })),
    [employees, leNameMap]
  )

  const employeeSource = React.useMemo(
    () =>
      createDataSourceDefinition({
        dataAdapter: {
          fetchData: ({ search }) => {
            const filtered = search
              ? employeeRecords.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
              : employeeRecords
            return { records: filtered }
          },
        },
        grouping: {
          mandatory: true,
          hideSelector: true,
          collapsible: true,
          defaultOpenGroups: false,
          groupBy: {
            legalEntityId: {
              name: i18n.t('universal_importer.unified_edition.legal_entities_group'),
              label: (groupId: unknown) => leNameMap.get(String(groupId)) ?? String(groupId),
            },
          },
        },
      }),
    [employeeRecords, leNameMap]
  )

  // --- Field data source ---
  const fieldRecords: FieldRecord[] = React.useMemo(() => {
    const records: FieldRecord[] = []
    for (const field of visibleFields) {
      const type = classifyField(field)
      let groupKey: string
      if (type === 'standard') {
        groupKey = field.category || 'others'
      } else if (type === 'custom') {
        groupKey = '__custom'
      } else {
        groupKey = '__country'
      }

      const disabled = !!(field.identifier || (isMultiLe && field.leDependant))

      let groupLabel = formatGroupTitle(
        groupKey === '__custom' ? 'custom' : groupKey === '__country' ? 'country' : groupKey
      )
      if (disabled && (groupKey === '__custom' || groupKey === '__country')) {
        groupLabel += ` (${i18n.t('universal_importer.field_selection.disabled_multi_le')})`
      }

      records.push({
        id: field.key,
        name: field.label,
        groupKey,
        groupLabel,
        disabled,
      })
    }
    // Sort: named categories first, then others, then country, then custom
    records.sort((a, b) => {
      const order = (key: string) => {
        if (key === 'others') return 1
        if (key === '__country') return 2
        if (key === '__custom') return 3
        return 0
      }
      return order(a.groupKey) - order(b.groupKey)
    })
    return records
  }, [visibleFields, isMultiLe])

  const fieldGroupLabelMap = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const r of fieldRecords) {
      if (!map.has(r.groupKey)) map.set(r.groupKey, r.groupLabel)
    }
    return map
  }, [fieldRecords])

  const fieldSource = React.useMemo(
    () =>
      createDataSourceDefinition({
        dataAdapter: {
          fetchData: ({ search }) => {
            const filtered = search
              ? fieldRecords.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
              : fieldRecords
            return { records: filtered }
          },
        },
        grouping: {
          mandatory: true,
          hideSelector: true,
          collapsible: true,
          defaultOpenGroups: false,
          groupBy: {
            groupKey: {
              name: i18n.t('universal_importer.unified_edition.field_categories_group'),
              label: (groupId: unknown) =>
                fieldGroupLabelMap.get(String(groupId)) ?? String(groupId),
            },
          },
        },
      }),
    [fieldRecords, fieldGroupLabelMap]
  )

  // --- Handlers ---
  const handleEmployeeChange = React.useCallback((value: string[]) => {
    setSelectedEmployeeIds(value)
  }, [])

  const handleFieldChange = React.useCallback(
    (value: string[]) => {
      // Always include identifier fields
      const withIdentifiers = [...value]
      for (const f of visibleFields) {
        if (f.identifier && !withIdentifiers.includes(f.key)) {
          withIdentifiers.push(f.key)
        }
      }
      // Remove LE-dependent fields when multi-LE
      if (isMultiLe) {
        const filtered = withIdentifiers.filter((key) => {
          const field = visibleFields.find((f) => f.key === key)
          return field && !field.leDependant
        })
        setSelectedFieldKeys(filtered)
      } else {
        setSelectedFieldKeys(withIdentifiers)
      }
    },
    [visibleFields, isMultiLe]
  )

  // Hidden fields must always be included (e.g. legal_entity_country for transformations)
  const hiddenFieldKeys = React.useMemo(
    () => fields.filter((f) => f.hidden).map((f) => f.key),
    [fields]
  )

  // Confirm handler
  const handleConfirm = React.useCallback(() => {
    if (selectedEmployeeIds.length === 0) return

    const resolved = resolveRequiredDependencies(selectedFieldKeys, fields)
    const leIds = Array.from(selectedLeIds)
    const allSelectedFields = [...new Set([...resolved.expandedSelection, ...hiddenFieldKeys])]

    onConfirm({
      selectedEmployeeIds,
      selectedFields: allSelectedFields,
      legalEntityIds: isMultiLe ? null : leIds.length > 0 ? leIds : null,
      isMultiLe,
    })
  }, [
    selectedEmployeeIds,
    selectedFieldKeys,
    fields,
    selectedLeIds,
    isMultiLe,
    onConfirm,
    hiddenFieldKeys,
  ])

  return (
    <F0Dialog
      isOpen={isOpen}
      title={i18n.t('universal_importer.unified_edition.title')}
      description={i18n.t('universal_importer.unified_edition.select_employees')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('universal_importer.unified_edition.apply'),
        onClick: handleConfirm,
        disabled: selectedEmployeeIds.length === 0,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
      width="sm"
    >
      {/* Employee selection */}
      <F0Select
        label={i18n.t('universal_importer.unified_edition.employees_label')}
        multiple
        source={employeeSource}
        mapOptions={(item: EmployeeRecord) => ({
          value: item.id,
          label: item.name,
        })}
        value={selectedEmployeeIds}
        onChange={handleEmployeeChange}
        showSearchBox
        searchBoxPlaceholder={i18n.t('universal_importer.unified_edition.employees_placeholder')}
        placeholder={i18n.t('universal_importer.unified_edition.employees_placeholder')}
        required
        showPreview={false}
      />

      {/* Field selection */}
      <F0Select
        label={i18n.t('universal_importer.unified_edition.fields_label')}
        multiple
        source={fieldSource}
        mapOptions={(item: FieldRecord) => ({
          value: item.id,
          label: item.name,
          disabled: item.disabled,
        })}
        value={selectedFieldKeys}
        onChange={handleFieldChange}
        showSearchBox
        searchBoxPlaceholder={i18n.t('universal_importer.unified_edition.fields_placeholder')}
        placeholder={i18n.t('universal_importer.unified_edition.fields_placeholder')}
        required
        showPreview={false}
      />

      {/* Multi-LE alert */}
      {isMultiLe && (
        <F0Alert
          variant="info"
          title={i18n.t('universal_importer.unified_edition.standard_fields_only_title')}
          description={i18n.t(
            'universal_importer.unified_edition.standard_fields_only_description'
          )}
        />
      )}
    </F0Dialog>
  )
}
